/**
 * Photo pipeline (v3 section 10): capture -> decode to a canvas -> resize so
 * the longest edge is 1200 px -> JPEG at quality 0.7 -> a Blob of roughly
 * 150-250 KB. Runs BEFORE the write to IndexedDB, so what is stored (and
 * later uploaded over a rural connection) is the small file, never the
 * camera's 4-12 MB original.
 *
 * Chrome/Android only (v3 section 9): createImageBitmap and OffscreenCanvas
 * are both available there. `imageOrientation: 'from-image'` applies the EXIF
 * rotation, so a portrait shot is stored upright.
 */
export const MAX_EDGE = 1200
export const JPEG_QUALITY = 0.7

export function targetDimensions(width: number, height: number, maxEdge = MAX_EDGE): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height }
  const scale = maxEdge / longest
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

export interface CompressedPhoto {
  blob: Blob
  width: number
  height: number
  originalBytes: number
}

// TypeScript 4.9's DOM lib (the app's compiler) predates these, so they are
// described here rather than silenced: the shapes Chrome actually provides.
type CreateImageBitmapWithOptions = (image: Blob, options: { imageOrientation: 'from-image' | 'none' }) => Promise<ImageBitmap>
interface OffscreenCanvasLike {
  getContext(kind: '2d'): { drawImage(image: ImageBitmap, x: number, y: number, w: number, h: number): void } | null
  convertToBlob(options: { type: string; quality: number }): Promise<Blob>
}
type OffscreenCanvasCtor = new (width: number, height: number) => OffscreenCanvasLike

export async function compressPhoto(file: Blob, maxEdge = MAX_EDGE, quality = JPEG_QUALITY): Promise<CompressedPhoto> {
  const bitmap = await (createImageBitmap as unknown as CreateImageBitmapWithOptions)(file, { imageOrientation: 'from-image' })
  try {
    const { width, height } = targetDimensions(bitmap.width, bitmap.height, maxEdge)
    let blob: Blob
    const Offscreen = (globalThis as unknown as { OffscreenCanvas?: OffscreenCanvasCtor }).OffscreenCanvas
    if (Offscreen) {
      const canvas = new Offscreen(width, height)
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('no 2d context')
      ctx.drawImage(bitmap, 0, 0, width, height)
      blob = await canvas.convertToBlob({ type: 'image/jpeg', quality })
    } else {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('no 2d context')
      ctx.drawImage(bitmap, 0, 0, width, height)
      blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', quality),
      )
    }
    return { blob, width, height, originalBytes: file.size }
  } finally {
    bitmap.close()
  }
}
